import { useApi } from "./useApi";
import { useMutation, useQuery } from "@tanstack/react-query";

/**
 * MT permit verification (MS-481/483). All endpoints return HTTP 200 — the
 * outcome is `result.state` (see VerificationState), never the HTTP status or the
 * envelope's isSuccess. Identity is a per-owner roster: a verify call carries
 * EITHER a raw `idNumber` (new owner, found-or-created) OR an `ownerIdentityId`
 * (an owner already on GET /owners) — never both, never neither.
 *
 * The verification controller serialises with Newtonsoft (PascalCase) while the
 * rest of the API is camelCase, so responses are read case-insensitively and
 * normalised to a stable camelCase shape for the UI.
 */

export enum VerificationState {
  Success = 0,
  InvalidPermit = 1,
  NoMatch = 2,
  ServiceUnavailable = 3,
  InvalidFormat = 4,
}

/** Per-property stored status (property list/details — MS-486/488). */
export enum PermitVerificationStatus {
  Unverified = 0,
  Pending = 1,
  Verified = 2,
  Failed = 3,
}

export interface PropertyVerificationResult {
  state: VerificationState;
  verifiedNameAr?: string | null;
  verifiedNameEn?: string | null;
  expiryDate?: string | null;
  nationalIdAlreadyOnFile?: boolean;
  /** The roster owner this call resolved to — reuse to bind more properties
   * without re-entering the National ID. */
  ownerIdentityId?: number | null;
}

export interface OwnerIdentityListItem {
  id: number;
  ownerNameAr?: string | null;
  ownerNameEn?: string | null;
  /** Masked National ID from the server (raw ID never leaves the backend). */
  maskedNationalId: string;
  /** 0 = Individual (V1), 1 = Business (reserved). */
  identityType: number;
}

export interface VerifyPermitRequest {
  permitNumber: string;
  /** New owner (raw ID). Omit when ownerIdentityId is supplied. */
  idNumber?: string;
  /** Existing roster owner. Omit when idNumber is supplied. */
  ownerIdentityId?: number;
}

/** Result of setting the host's default owner National ID from Settings (MS-489). */
export interface SetDefaultIdentityResult {
  state: VerificationState;
  ownerIdentityId?: number | null;
  maskedNationalId?: string | null;
}

/** Read a field regardless of Pascal/camel casing. */
const ci = (o: any, key: string): any => {
  if (o == null) return undefined;
  const lower = key.charAt(0).toLowerCase() + key.slice(1);
  const upper = key.charAt(0).toUpperCase() + key.slice(1);
  return o[key] ?? o[lower] ?? o[upper];
};

const envelope = (data: any): any => ci(data, "Result");

const normalizeResult = (r: any): PropertyVerificationResult => ({
  state: ci(r, "State") as VerificationState,
  verifiedNameAr: ci(r, "VerifiedNameAr") ?? null,
  verifiedNameEn: ci(r, "VerifiedNameEn") ?? null,
  expiryDate: ci(r, "ExpiryDate") ?? null,
  nationalIdAlreadyOnFile: ci(r, "NationalIdAlreadyOnFile") ?? false,
  ownerIdentityId: ci(r, "OwnerIdentityId") ?? null,
});

const normalizeOwner = (o: any): OwnerIdentityListItem => ({
  id: ci(o, "Id"),
  ownerNameAr: ci(o, "OwnerNameAr") ?? null,
  ownerNameEn: ci(o, "OwnerNameEn") ?? null,
  maskedNationalId: ci(o, "MaskedNationalId") ?? "",
  identityType: ci(o, "IdentityType") ?? 0,
});

const normalizeSetIdentity = (r: any): SetDefaultIdentityResult => ({
  state: ci(r, "State") as VerificationState,
  ownerIdentityId: ci(r, "OwnerIdentityId") ?? null,
  maskedNationalId: ci(r, "MaskedNationalId") ?? null,
});

/** Send exactly one identity source; PascalCase to match the C# request DTO. */
const toBackendBody = (b: VerifyPermitRequest) => ({
  PermitNumber: b.permitNumber,
  ...(b.ownerIdentityId != null ? { OwnerIdentityId: b.ownerIdentityId } : {}),
  ...(b.idNumber ? { IdNumber: b.idNumber } : {}),
});

const useVerification = () => {
  const { apiPrivate } = useApi();

  /** The caller's owner roster (masked). Drives the saved-ID picker/radio. */
  const getOwnersQuery = (enabled = true) =>
    useQuery({
      queryKey: ["verification-owners"],
      enabled,
      queryFn: async (): Promise<OwnerIdentityListItem[]> => {
        const { data } = await apiPrivate.get("/api/verification/owners");
        const list = envelope(data);
        return Array.isArray(list) ? list.map(normalizeOwner) : [];
      },
    });

  /** Standalone onboarding pre-check (no property yet). */
  const verifyIdentityMutation = () =>
    useMutation({
      mutationFn: async (body: VerifyPermitRequest): Promise<PropertyVerificationResult> => {
        const { data } = await apiPrivate.post("/api/verification/verify", toBackendBody(body));
        return normalizeResult(envelope(data));
      },
    });

  /** Re-verify an existing property (dashboard/management — persists). */
  const verifyPropertyMutation = (propertyId: number) =>
    useMutation({
      mutationFn: async (body: VerifyPermitRequest): Promise<PropertyVerificationResult> => {
        const { data } = await apiPrivate.post(
          `/api/verification/property/${propertyId}/verify`,
          toBackendBody(body)
        );
        return normalizeResult(envelope(data));
      },
    });

  /** Settings (MS-489): set the host's default owner National ID (no permit).
   * Backend stores it on the primary roster owner and re-verifies the host's
   * properties; state is Success or InvalidFormat. */
  const setDefaultIdentityMutation = () =>
    useMutation({
      mutationFn: async (idNumber: string): Promise<SetDefaultIdentityResult> => {
        const { data } = await apiPrivate.post("/api/verification/identity", {
          IdNumber: idNumber,
        });
        return normalizeSetIdentity(envelope(data));
      },
    });

  return {
    getOwnersQuery,
    verifyIdentityMutation,
    verifyPropertyMutation,
    setDefaultIdentityMutation,
  };
};

export { useVerification };
